import cronstrue from "cronstrue";
import moment from "moment-timezone";
import ApiService from "./api.service";

const UtilService = {
  store: null,
  init(opts) {
    if (opts.store) {
      this.store = opts.store;
    }
  },

  parseDateClient(dateString, format, client) {
    let timezone = client && client.timezone ? client.timezone : moment.tz.guess();
    return dateString && dateString.length > 0 ? moment.tz(dateString, format, timezone) : "";
  },
  formatDateClient(utcDateString, format, client) {
    let timezone = client && client.timezone ? client.timezone : moment.tz.guess();
    return utcDateString && utcDateString.length > 0 ? moment.tz(utcDateString, timezone).format(format) : "";
  },
  formatStage(claimStage, claim) {
    let stageName = claimStage.name;
    if (claim.approved === true && claimStage.nameWhenApproved && claimStage.nameWhenApproved.trim().length > 0) {
      stageName = claimStage.nameWhenApproved;
    } else if (
      claim.rejected === true &&
      claimStage.nameWhenRejected &&
      claimStage.nameWhenRejected.trim().length > 0
    ) {
      stageName = claimStage.nameWhenRejected;
    }
    return stageName;
  },
  isEffective(objectWithEffectivity, momentDate) {
    let now = momentDate ? moment(momentDate.format("YYYY-MM-DDTHH:mm:ss.SSSSZ")) : moment();

    let active = false;
    const effectiveDate = objectWithEffectivity.effectiveDate ? moment(objectWithEffectivity.effectiveDate) : null;
    const expirationDate = objectWithEffectivity.expirationDate ? moment(objectWithEffectivity.expirationDate) : null;

    if (effectiveDate && expirationDate) {
      active = now.isBetween(effectiveDate, expirationDate, undefined, "[]") || now.isSame(effectiveDate);
    } else if (effectiveDate) {
      active = now.isSameOrAfter(effectiveDate);
    } else if (expirationDate) {
      active = now.isSameOrBefore(expirationDate);
    } else {
      active = true;
    }
    return active;
  },
  isStillClaimable(objectWithEffectivity) {
    let now = moment();
    let active = false;
    if (objectWithEffectivity.effectiveDate && objectWithEffectivity.submissionDeadlineDate) {
      active = now.isBetween(
        moment(objectWithEffectivity.effectiveDate),
        moment(objectWithEffectivity.submissionDeadlineDate)
      );
    } else if (objectWithEffectivity.effectiveDate) {
      active = now.isSameOrAfter(objectWithEffectivity.effectiveDate);
    } else if (objectWithEffectivity.submissionDeadlineDate) {
      active = now.isSameOrBefore(objectWithEffectivity.submissionDeadlineDate);
    } else {
      active = true;
    }
    return active;
  },
  toCronReadable(value) {
    return value != null ? cronstrue.toString(value) : "";
  },
  toCurrency(value) {
    let number = parseFloat(value);

    if (isNaN(number)) {
      return value;
    }
    var formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2
    });
    return formatter.format(number);
  },
  toPhoneNumber(value) {
    // Filter only numbers from the input
    let cleaned = ("" + value).replace(/\D/g, "");

    // Check if the input is of correct
    let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      // Remove the matched extension code
      // Change this to format for any country code.
      let intlCode = match[1] ? "+1 " : "";
      return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
    }

    return value;
  },
  toNumber(value, decimals, program) {
    if (typeof value !== "number") {
      return value;
    }
    if (program && program.awardUnitsEnabled && program.awardUnitsDecimalPlace > -1) {
      decimals = program.awardUnitsDecimalPlace;
    }
    var formatter;
    if (decimals) {
      value = value.toFixed(decimals);
      formatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: decimals });
    } else {
      formatter = new Intl.NumberFormat("en-US");
    }
    return formatter.format(value);
  },
  isNumber(input) {
    let numericInput = parseInt(input);
    return typeof numericInput === "number" && !isNaN(numericInput);
  },
  toPropercase(value) {
    return value.replace(/\w\S*/g, function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  },
  getDateInClientTimezone(date) {
    let client = this.store.getters.selectedClient.timezone;
    let timezone = client && client.timezone ? client.timezone : moment.tz.guess();
    return moment.tz(date, timezone);
  },

  /**
   * Loads all pages from a paginated POST endpoint and returns a single flattened array.
   *
   * @param {string} url
   *  Base endpoint URL (without pagination params). Example: "/api/participants/search"
   *
   * @param {Object} payload
   *  Request body sent in the POST call
   *
   * @param {number} [pageSize=100]
   *  Number of items per page to request from the API.
   *
   * @param {string[]} [expands=[]]
   *  Optional list of "expand" values to include in the query string.
   *  Example: ["organization", "programGroup"]
   *
   * @returns {Promise<Array>}
   *  A promise that resolves with all items from all pages combined into a single array.
   */
  async fetchPaginatedData(url, payload, pageSize = 100, expands = []) {
    let fullItems = [];
    let currentPage = 0;
    let totalPages = 1;

    const baseParams = new URLSearchParams();
    baseParams.set("size", pageSize);

    if (Array.isArray(expands) && expands.length > 0) {
      expands.filter(Boolean).forEach(e => baseParams.append("expand", e));
    }

    while (currentPage < totalPages) {
      const params = new URLSearchParams(baseParams);
      params.set("page", currentPage);

      const response = await ApiService.post(`${url}?${params.toString()}`, payload);

      const data = response.data;
      fullItems = fullItems.concat(data?.content ?? []);
      totalPages = data?.totalPages ?? 1;
      currentPage++;
    }

    return fullItems;
  },

  isUnder18(date) {
    if (!date) {
      return false;
    }
    const birthDate = moment(date);
    const today = moment();
    const age = today.diff(birthDate, "years");
    return age < 18;
  },

  buildPaginationParams(options) {
    const { sortBy, sortDesc, page, itemsPerPage } = options;

    const params = new URLSearchParams();
    params.append("size", itemsPerPage);
    params.append("page", page - 1);

    if (sortBy && sortBy.length > 0) {
      const direction = sortDesc?.[0] ? "DESC" : "ASC";
      params.append("sort", `${sortBy[0]},${direction}`);
    }

    return params;
  },

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
};

export default UtilService;
